/**
 * Shared zod building blocks for every form in the app. Reused so email/phone
 * format rules live in exactly one place instead of being copy-pasted (and
 * silently drifting) per form.
 */
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .regex(EMAIL_PATTERN, 'Enter a valid email address');

export const optionalEmailSchema = z
  .union([z.literal(''), emailSchema])
  .optional();

export const phoneSchema = z
  .string()
  .trim()
  .min(1, 'Phone number is required')
  .refine((v) => isValidPhoneNumber(v, 'NG'), 'Enter a valid phone number');

export const optionalPhoneSchema = z
  .union([z.literal(''), phoneSchema])
  .optional();

export function requiredString(label: string, min = 1, max = 255) {
  return z
    .string()
    .trim()
    .min(min, `${label} is required`)
    .max(max, `${label} must be ${max} characters or fewer`);
}

export function optionalString(max = 2000) {
  return z.string().trim().max(max).optional();
}

/** Currency amount in naira (major units) — positive, finite, at most 2dp. */
export const positiveAmount = z
  .coerce.number({ message: 'Enter a valid amount' })
  .positive('Amount must be greater than zero')
  .refine((n) => Number.isFinite(n) && Math.round(n * 100) === n * 100, {
    message: 'Amount can have at most 2 decimal places',
  });

export const dateStringSchema = z
  .string()
  .min(1, 'Date is required')
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Enter a valid date');

/** Matches the backend's own asset-path rule: a relative path OR a full http(s) URL, never javascript:/data:. */
const RELATIVE_OR_HTTP_URL_PATTERN = /^(\/[^\s]*|https?:\/\/[^\s]+)$/i;

export const relativeOrHttpUrlSchema = z
  .union([
    z.literal(''),
    z
      .string()
      .trim()
      .regex(RELATIVE_OR_HTTP_URL_PATTERN, 'Must be a relative path (starting with /) or an http(s) URL'),
  ])
  .optional();

export function latitudeStringSchema(label = 'Latitude') {
  return z
    .string()
    .optional()
    .refine(
      (v) => !v || (Number.isFinite(Number(v)) && Number(v) >= -90 && Number(v) <= 90),
      `${label} must be between -90 and 90`,
    );
}

export function longitudeStringSchema(label = 'Longitude') {
  return z
    .string()
    .optional()
    .refine(
      (v) => !v || (Number.isFinite(Number(v)) && Number(v) >= -180 && Number(v) <= 180),
      `${label} must be between -180 and 180`,
    );
}
